import type { ErrorEvent } from "@sentry/browser";

import { generalizeMessage } from "./messageGeneralize";
import { redactCredentialsInText } from "./urlGeneralize";

type MessageSource = {
	message?: string;
	exception?: { values?: { value?: string }[] };
};

export const getEventMessage = (event: MessageSource | undefined): string | undefined =>
	event?.exception?.values?.[0]?.value ?? event?.message;

const scrub = (value: string | undefined) =>
	typeof value === "string" ? redactCredentialsInText(value) : value;

export const snapshotEventMessages = (event: MessageSource): MessageSource => ({
	message: scrub(event.message),
	exception: { values: event.exception?.values?.map(({ value }) => ({ value: scrub(value) })) },
});

export const generalizeEventMessages = (
	event: ErrorEvent,
	original: MessageSource = snapshotEventMessages(event)
) => {
	if (typeof event.message === "string") event.message = generalizeMessage(event.message);
	event.exception?.values?.forEach(exception => {
		if (typeof exception.value === "string") exception.value = generalizeMessage(exception.value);
	});

	const originalMessage = getEventMessage(original);
	const originals: Record<string, string> = {};
	const remember = (field: string, before: string | undefined, after: string | undefined) => {
		if (before !== undefined && before !== after) originals[field] = before;
	};
	remember("message", original.message, event.message);
	original.exception?.values?.forEach(({ value }, index) => {
		remember(`exception.values.${index}.value`, value, event.exception?.values?.[index]?.value);
	});
	if (!Object.keys(originals).length) return;

	const extra = { ...event.extra };
	if (originalMessage !== undefined && originalMessage !== getEventMessage(event)) {
		// Preserve complete diagnostics; transport handles the keepalive limit for large payloads.
		extra.original_message ??= originalMessage;
	}
	const additional = Object.fromEntries(
		Object.entries(originals).filter(([, value]) => value !== extra.original_message)
	);
	if (Object.keys(additional).length) {
		const previous = extra.original_message_fields;
		// Keep the earliest captured value if the same event is processed again.
		extra.original_message_fields = {
			...additional,
			...(previous && typeof previous === "object" ? previous : {}),
		};
	}
	event.extra = extra;
};
